import type { TargetLanguage } from "./languages";

export type UiCopy = {
  appName: string;
  metaDescription: string;
  setupEyebrow: string;
  outputLanguagePrompt: string;
  outputLanguageHelp: string;
  textOnly: string;
  textOnlyDescription: string;
  textAndAudio: string;
  textAndAudioDescription: string;
  headphonesRecommended: string;
  audioDevices: string;
  audioDevicesHelp: string;
  microphone: string;
  audioOutput: string;
  systemDefault: string;
  systemOutputGuidance: string;
  outputSelectionUnsupported: string;
  inputLevel: string;
  testMicrophone: string;
  testAudio: string;
  startListening: string;
  preparingMicrophone: string;
  connectingTranslation: string;
  stopListening: string;
  liveTranslation: string;
  transcriptEmpty: string;
  muteMicrophone: string;
  unmuteMicrophone: string;
  turnAudioOn: string;
  turnAudioOff: string;
  showText: string;
  hideText: string;
  volume: string;
  changeLanguage: string;
  leave: string;
  retry: string;
  tryAgain: string;
  statusIdle: string;
  statusConnecting: string;
  statusListening: string;
  statusReconnecting: string;
  statusMuted: string;
  statusError: string;
  errorMicrophonePermission: string;
  errorMicrophoneTimeout: string;
  errorMicrophoneBusy: string;
  errorUnsupportedBrowser: string;
  errorOffline: string;
  errorNoMicrophone: string;
  errorConnection: string;
  errorConnectionTimeout: string;
  errorAudioPlayback: string;
  navigationLabel: string;
  openHostRecorder: string;
  openParticipantTranslator: string;
  openMeetingArchive: string;
  backToHostRecorder: string;
  archiveSignInRequired: string;
  recordingSavedMessage: string;
  viewMeeting: string;
  dismissNotification: string;
  hostAccessTitle: string;
  hostAccessDescription: string;
  hostEmailLabel: string;
  hostPasswordLabel: string;
  hostLoginAction: string;
  hostLoggingInAction: string;
  hostSignedInLabel: string;
  hostLogoutAction: string;
  hostLoggingOutAction: string;
  hostMeetingTitleLabel: string;
  hostMeetingTitlePlaceholder: string;
  hostConsentLabel: string;
  hostConsentHelp: string;
  hostLoginError: string;
  recordingPreparingStatus: string;
  recordingActiveStatus: string;
  recordingUploadingStatus: string;
  recordingFinalizingStatus: string;
  recordingReadyStatus: string;
  recordingFailedStatus: string;
  recordingElapsedLabel: string;
  recordingPendingChunksLabel: string;
  recordingAcknowledgedChunkLabel: string;
  recordingNoAcknowledgedChunk: string;
  recordingPlaybackLabel: string;
  recordingRetryAction: string;
  recordingConfirmStop: string;
  recordingErrorStart: string;
  recordingErrorUnsupported: string;
  recordingErrorMicrophone: string;
  recordingErrorUpload: string;
  recordingErrorRecorder: string;
  recordingErrorNoAudio: string;
  recordingErrorFinalize: string;
  recordingWarningStorage: string;
  recordingRecoveryTitle: string;
  recordingRecoveryDescription: string;
  recordingRecoveryAction: string;
  recordingRecoveryRemaining: string;
  recordingDiscardAction: string;
  recordingDiscardConfirm: string;
  archiveTitle: string;
  archiveDescription: string;
  archiveRefresh: string;
  archiveLoading: string;
  archiveLoadError: string;
  archiveEmpty: string;
  archiveNoResults: string;
  archiveSelectPrompt: string;
  archiveDetailError: string;
  archiveSearchLabel: string;
  archiveSearchPlaceholder: string;
  archiveStatusFilterLabel: string;
  archiveStatusAll: string;
  archiveAudioTitle: string;
  archiveAudioUnavailable: string;
  archiveDownloadAudio: string;
  archiveProcessingTitle: string;
  archiveProcessingQueued: string;
  archiveProcessingRunning: string;
  archiveProcessingReady: string;
  archiveProcessingFailed: string;
  archiveProcessingError: string;
  archiveRetryProcessing: string;
  archiveRetryingProcessing: string;
  archiveTranscriptTitle: string;
  archiveTranscriptPending: string;
  archiveTranscriptEmpty: string;
  archiveDownloadTranscript: string;
  archiveViewTranscript: string;
  archiveBackToArchive: string;
  archivePlayFromTimestamp: string;
  archiveRenameSpeaker: string;
  archiveSpeakerNameLabel: string;
  archiveSaveSpeaker: string;
  archiveCancel: string;
  archiveSpeakerSaveError: string;
  archiveSummaryTitle: string;
  archiveSummaryPending: string;
  archiveSummaryEmpty: string;
  archiveDownloadSummary: string;
  archiveOverview: string;
  archiveKeyPoints: string;
  archiveDecisions: string;
  archiveActionItems: string;
  archiveOpenQuestions: string;
  archiveStatusRecording: string;
  archiveStatusFinalizing: string;
  archiveStatusReady: string;
  archiveStatusFailed: string;
  archiveStatusProcessing: string;
  archiveLocaleEnglish: string;
  archiveLocaleIndonesian: string;
  archiveLocaleChinese: string;
  archiveLocaleThai: string;
};

export type UiCopyKey = keyof UiCopy;

export const UI_COPY: Record<TargetLanguage, UiCopy> = {
  en: {
    appName: "Table Interpreter",
    metaDescription: "Follow a same-table conversation in the language you understand.",
    setupEyebrow: "Same table · shared conversation",
    outputLanguagePrompt: "What do you want to understand?",
    outputLanguageHelp: "Choose the language you want to read or hear.",
    textOnly: "Text only",
    textOnlyDescription: "Follow the translation quietly on screen.",
    textAndAudio: "Text and audio",
    textAndAudioDescription: "Read the translation and hear it as it arrives.",
    headphonesRecommended: "Headphones recommended to prevent echo at the table.",
    audioDevices: "Audio devices",
    audioDevicesHelp: "Check the microphone and listening output before starting.",
    microphone: "Microphone",
    audioOutput: "Audio output",
    systemDefault: "System default",
    systemOutputGuidance: "Translated audio uses the output selected here.",
    outputSelectionUnsupported: "Choose the audio output in Control Center or your system settings.",
    inputLevel: "Input level",
    testMicrophone: "Test microphone",
    testAudio: "Test audio",
    startListening: "Start listening",
    preparingMicrophone: "Preparing microphone…",
    connectingTranslation: "Connecting…",
    stopListening: "Stop listening",
    liveTranslation: "Live translation",
    transcriptEmpty: "Translation will appear here when someone speaks.",
    muteMicrophone: "Turn off microphone",
    unmuteMicrophone: "Turn on microphone",
    turnAudioOn: "Turn on audio",
    turnAudioOff: "Turn off audio",
    showText: "Show text",
    hideText: "Hide text",
    volume: "Volume",
    changeLanguage: "Change language",
    leave: "Leave",
    retry: "Retry",
    tryAgain: "Try again",
    statusIdle: "Ready",
    statusConnecting: "Connecting",
    statusListening: "Listening",
    statusReconnecting: "Reconnecting",
    statusMuted: "Microphone off",
    statusError: "Connection failed",
    errorMicrophonePermission: "Allow microphone access to translate the conversation.",
    errorMicrophoneTimeout: "Microphone permission is taking too long. Check the browser prompt or site settings; this screen will continue when you respond.",
    errorMicrophoneBusy: "The microphone is unavailable or in use by another app. Close it there, then try again.",
    errorUnsupportedBrowser: "This browser cannot access the microphone. Open this link in Safari or Chrome and try again.",
    errorOffline: "You appear to be offline. Reconnect to the internet and try again.",
    errorNoMicrophone: "No microphone was found. Connect one and try again.",
    errorConnection: "The translation connection was interrupted. Try again.",
    errorConnectionTimeout: "The translation could not connect in time. Check your internet connection and try again.",
    errorAudioPlayback: "Audio could not play. Check your output and try again.",
    navigationLabel: "Navigation",
    openHostRecorder: "Open host recorder",
    openParticipantTranslator: "Participant translator",
    openMeetingArchive: "Meeting archive",
    backToHostRecorder: "Back to host recorder",
    archiveSignInRequired: "Sign in on the host recorder to open the protected meeting archive.",
    recordingSavedMessage: "Meeting saved. The recording was uploaded and finalized; transcript and summaries are now processing.",
    viewMeeting: "View meeting",
    dismissNotification: "Dismiss notification",
    hostAccessTitle: "Host recorder",
    hostAccessDescription: "Superusers can create the protected meeting recording.",
    hostEmailLabel: "Email",
    hostPasswordLabel: "Password",
    hostLoginAction: "Sign in",
    hostLoggingInAction: "Signing in…",
    hostSignedInLabel: "Signed in as",
    hostLogoutAction: "Sign out",
    hostLoggingOutAction: "Signing out…",
    hostMeetingTitleLabel: "Meeting title",
    hostMeetingTitlePlaceholder: "e.g. Supplier discussion",
    hostConsentLabel: "Everyone at the table has consented to recording.",
    hostConsentHelp: "Keep this phone unlocked and on this screen until the meeting is finalized.",
    hostLoginError: "Host sign-in failed. Check the details and try again.",
    recordingPreparingStatus: "Preparing recording",
    recordingActiveStatus: "REC · Recording",
    recordingUploadingStatus: "Finishing upload",
    recordingFinalizingStatus: "Finalizing recording",
    recordingReadyStatus: "Recording ready",
    recordingFailedStatus: "Recording needs attention",
    recordingElapsedLabel: "Elapsed",
    recordingPendingChunksLabel: "Pending",
    recordingAcknowledgedChunkLabel: "Stored chunk",
    recordingNoAcknowledgedChunk: "None yet",
    recordingPlaybackLabel: "Protected meeting recording",
    recordingRetryAction: "Retry finalization",
    recordingConfirmStop: "Stop and finalize this meeting recording? Keep this screen open until it finishes.",
    recordingErrorStart: "The protected recording could not start. Keep this screen open and try again.",
    recordingErrorUnsupported: "This browser cannot create a meeting recording. Use the latest Chrome or Safari.",
    recordingErrorMicrophone: "The recording microphone disconnected. Stop and save the meeting now.",
    recordingErrorUpload: "Some audio is not stored yet. Keep this screen open, then stop or retry to upload it.",
    recordingErrorRecorder: "The browser recorder stopped unexpectedly. Stop and save the meeting now.",
    recordingErrorNoAudio: "The recording did not produce any audio. Check the microphone before trying again.",
    recordingErrorFinalize: "The recording could not be finalized yet. Keep this screen open and retry.",
    recordingWarningStorage: "Local recovery storage is unavailable. Keep this screen open until every pending chunk is stored.",
    recordingRecoveryTitle: "Unfinished recording found",
    recordingRecoveryDescription: "Recover stored audio and finish:",
    recordingRecoveryAction: "Recover and finalize",
    recordingRecoveryRemaining: "unfinished recordings remain",
    recordingDiscardAction: "Discard local copy",
    recordingDiscardConfirm: "Discard the local recovery copy? Audio that was not uploaded cannot be recovered.",
    archiveTitle: "Meeting archive",
    archiveDescription: "Shared recordings, transcripts, and summaries for all superusers.",
    archiveRefresh: "Refresh",
    archiveLoading: "Loading meeting archive…",
    archiveLoadError: "The meeting archive could not be loaded.",
    archiveEmpty: "No recorded meetings yet.",
    archiveNoResults: "No meetings match your search.",
    archiveSelectPrompt: "Select a meeting to view its protected record.",
    archiveDetailError: "This meeting record could not be loaded.",
    archiveSearchLabel: "Search meetings",
    archiveSearchPlaceholder: "Search title, date, or status",
    archiveStatusFilterLabel: "Filter meetings by status",
    archiveStatusAll: "All statuses",
    archiveAudioTitle: "Recording",
    archiveAudioUnavailable: "The protected recording is not ready yet.",
    archiveDownloadAudio: "Download audio",
    archiveProcessingTitle: "Processing",
    archiveProcessingQueued: "Waiting to process transcript and summaries",
    archiveProcessingRunning: "Creating transcript and multilingual summaries",
    archiveProcessingReady: "Transcript and summaries are ready",
    archiveProcessingFailed: "Processing needs attention",
    archiveProcessingError: "Processing failed. Refresh later or ask an administrator to retry it.",
    archiveRetryProcessing: "Retry processing",
    archiveRetryingProcessing: "Retrying…",
    archiveTranscriptTitle: "Speaker transcript",
    archiveTranscriptPending: "The diarized transcript will appear when processing finishes.",
    archiveTranscriptEmpty: "No transcript is available for this meeting.",
    archiveDownloadTranscript: "Download transcript",
    archiveViewTranscript: "Open speaker transcript",
    archiveBackToArchive: "Back to meeting archive",
    archivePlayFromTimestamp: "Play recording from",
    archiveRenameSpeaker: "Rename speaker",
    archiveSpeakerNameLabel: "Speaker name",
    archiveSaveSpeaker: "Save speaker name",
    archiveCancel: "Cancel",
    archiveSpeakerSaveError: "The speaker name could not be saved.",
    archiveSummaryTitle: "Meeting summary",
    archiveSummaryPending: "This language summary is still being prepared.",
    archiveSummaryEmpty: "No summary is available in this language.",
    archiveDownloadSummary: "Download summary",
    archiveOverview: "Overview",
    archiveKeyPoints: "Key points",
    archiveDecisions: "Decisions",
    archiveActionItems: "Action items",
    archiveOpenQuestions: "Open questions",
    archiveStatusRecording: "Recording",
    archiveStatusFinalizing: "Finalizing",
    archiveStatusReady: "Ready",
    archiveStatusFailed: "Failed",
    archiveStatusProcessing: "Processing",
    archiveLocaleEnglish: "English",
    archiveLocaleIndonesian: "Bahasa Indonesia",
    archiveLocaleChinese: "简体中文",
    archiveLocaleThai: "ไทย",
  },
  id: {
    appName: "Penerjemah Meja",
    metaDescription: "Ikuti percakapan satu meja dalam bahasa yang Anda pahami.",
    setupEyebrow: "Satu meja · satu percakapan",
    outputLanguagePrompt: "Apa yang ingin Anda pahami?",
    outputLanguageHelp: "Pilih bahasa yang ingin Anda baca atau dengar.",
    textOnly: "Teks saja",
    textOnlyDescription: "Ikuti terjemahan dengan tenang di layar.",
    textAndAudio: "Teks dan audio",
    textAndAudioDescription: "Baca dan dengarkan terjemahan saat tersedia.",
    headphonesRecommended: "Disarankan memakai headphone agar tidak terjadi gema di meja.",
    audioDevices: "Perangkat audio",
    audioDevicesHelp: "Periksa mikrofon dan keluaran audio sebelum memulai.",
    microphone: "Mikrofon",
    audioOutput: "Keluaran audio",
    systemDefault: "Bawaan sistem",
    systemOutputGuidance: "Audio terjemahan menggunakan keluaran yang dipilih di sini.",
    outputSelectionUnsupported: "Pilih keluaran audio di Pusat Kontrol atau pengaturan sistem Anda.",
    inputLevel: "Level masukan",
    testMicrophone: "Uji mikrofon",
    testAudio: "Uji audio",
    startListening: "Mulai mendengarkan",
    preparingMicrophone: "Menyiapkan mikrofon…",
    connectingTranslation: "Menghubungkan…",
    stopListening: "Berhenti mendengarkan",
    liveTranslation: "Terjemahan langsung",
    transcriptEmpty: "Terjemahan akan muncul di sini saat seseorang berbicara.",
    muteMicrophone: "Matikan mikrofon",
    unmuteMicrophone: "Nyalakan mikrofon",
    turnAudioOn: "Nyalakan audio",
    turnAudioOff: "Matikan audio",
    showText: "Tampilkan teks",
    hideText: "Sembunyikan teks",
    volume: "Volume",
    changeLanguage: "Ubah bahasa",
    leave: "Keluar",
    retry: "Coba lagi",
    tryAgain: "Coba lagi",
    statusIdle: "Siap",
    statusConnecting: "Menghubungkan",
    statusListening: "Mendengarkan",
    statusReconnecting: "Menghubungkan kembali",
    statusMuted: "Mikrofon mati",
    statusError: "Koneksi gagal",
    errorMicrophonePermission: "Izinkan akses mikrofon untuk menerjemahkan percakapan.",
    errorMicrophoneTimeout: "Izin mikrofon memerlukan waktu terlalu lama. Periksa permintaan browser atau pengaturan situs; layar ini akan berlanjut setelah Anda merespons.",
    errorMicrophoneBusy: "Mikrofon tidak tersedia atau sedang digunakan aplikasi lain. Tutup di sana, lalu coba lagi.",
    errorUnsupportedBrowser: "Browser ini tidak dapat mengakses mikrofon. Buka tautan ini di Safari atau Chrome, lalu coba lagi.",
    errorOffline: "Anda tampaknya sedang offline. Sambungkan kembali internet lalu coba lagi.",
    errorNoMicrophone: "Mikrofon tidak ditemukan. Sambungkan mikrofon lalu coba lagi.",
    errorConnection: "Koneksi terjemahan terputus. Coba lagi.",
    errorConnectionTimeout: "Terjemahan tidak dapat terhubung tepat waktu. Periksa koneksi internet lalu coba lagi.",
    errorAudioPlayback: "Audio tidak dapat diputar. Periksa keluaran lalu coba lagi.",
    navigationLabel: "Navigasi",
    openHostRecorder: "Buka perekam host",
    openParticipantTranslator: "Penerjemah peserta",
    openMeetingArchive: "Arsip rapat",
    backToHostRecorder: "Kembali ke perekam host",
    archiveSignInRequired: "Masuk di halaman perekam host untuk membuka arsip rapat yang dilindungi.",
    recordingSavedMessage: "Rapat tersimpan. Rekaman telah diunggah dan diselesaikan; transkrip dan ringkasan kini sedang diproses.",
    viewMeeting: "Lihat rapat",
    dismissNotification: "Tutup notifikasi",
    hostAccessTitle: "Perekam host",
    hostAccessDescription: "Superuser dapat membuat rekaman rapat yang dilindungi.",
    hostEmailLabel: "Email",
    hostPasswordLabel: "Kata sandi",
    hostLoginAction: "Masuk",
    hostLoggingInAction: "Sedang masuk…",
    hostSignedInLabel: "Masuk sebagai",
    hostLogoutAction: "Keluar",
    hostLoggingOutAction: "Sedang keluar…",
    hostMeetingTitleLabel: "Judul rapat",
    hostMeetingTitlePlaceholder: "mis. Diskusi pemasok",
    hostConsentLabel: "Semua orang di meja telah menyetujui perekaman.",
    hostConsentHelp: "Biarkan ponsel tetap terbuka di layar ini sampai rapat selesai diproses.",
    hostLoginError: "Gagal masuk sebagai host. Periksa data lalu coba lagi.",
    recordingPreparingStatus: "Menyiapkan rekaman",
    recordingActiveStatus: "REC · Merekam",
    recordingUploadingStatus: "Menyelesaikan unggahan",
    recordingFinalizingStatus: "Menyelesaikan rekaman",
    recordingReadyStatus: "Rekaman siap",
    recordingFailedStatus: "Rekaman perlu diperiksa",
    recordingElapsedLabel: "Durasi",
    recordingPendingChunksLabel: "Tertunda",
    recordingAcknowledgedChunkLabel: "Bagian tersimpan",
    recordingNoAcknowledgedChunk: "Belum ada",
    recordingPlaybackLabel: "Rekaman rapat terlindungi",
    recordingRetryAction: "Coba selesaikan lagi",
    recordingConfirmStop: "Hentikan dan selesaikan rekaman rapat ini? Biarkan layar ini terbuka sampai selesai.",
    recordingErrorStart: "Rekaman terlindungi tidak dapat dimulai. Biarkan layar ini terbuka lalu coba lagi.",
    recordingErrorUnsupported: "Browser ini tidak dapat membuat rekaman rapat. Gunakan Chrome atau Safari versi terbaru.",
    recordingErrorMicrophone: "Mikrofon rekaman terputus. Hentikan dan simpan rapat sekarang.",
    recordingErrorUpload: "Sebagian audio belum tersimpan. Biarkan layar ini terbuka, lalu hentikan atau coba lagi untuk mengunggahnya.",
    recordingErrorRecorder: "Perekam browser berhenti tiba-tiba. Hentikan dan simpan rapat sekarang.",
    recordingErrorNoAudio: "Rekaman tidak menghasilkan audio. Periksa mikrofon sebelum mencoba lagi.",
    recordingErrorFinalize: "Rekaman belum dapat diselesaikan. Biarkan layar ini terbuka lalu coba lagi.",
    recordingWarningStorage: "Penyimpanan pemulihan lokal tidak tersedia. Biarkan layar ini terbuka sampai semua bagian tersimpan.",
    recordingRecoveryTitle: "Rekaman yang belum selesai ditemukan",
    recordingRecoveryDescription: "Pulihkan audio tersimpan dan selesaikan:",
    recordingRecoveryAction: "Pulihkan dan selesaikan",
    recordingRecoveryRemaining: "rekaman yang belum selesai tersisa",
    recordingDiscardAction: "Buang salinan lokal",
    recordingDiscardConfirm: "Buang salinan pemulihan lokal? Audio yang belum diunggah tidak dapat dipulihkan.",
    archiveTitle: "Arsip rapat",
    archiveDescription: "Rekaman, transkrip, dan ringkasan bersama untuk semua superuser.",
    archiveRefresh: "Muat ulang",
    archiveLoading: "Memuat arsip rapat…",
    archiveLoadError: "Arsip rapat tidak dapat dimuat.",
    archiveEmpty: "Belum ada rapat yang direkam.",
    archiveNoResults: "Tidak ada rapat yang cocok dengan pencarian Anda.",
    archiveSelectPrompt: "Pilih rapat untuk melihat catatan terlindunginya.",
    archiveDetailError: "Catatan rapat ini tidak dapat dimuat.",
    archiveSearchLabel: "Cari rapat",
    archiveSearchPlaceholder: "Cari judul, tanggal, atau status",
    archiveStatusFilterLabel: "Saring rapat berdasarkan status",
    archiveStatusAll: "Semua status",
    archiveAudioTitle: "Rekaman",
    archiveAudioUnavailable: "Rekaman terlindungi belum siap.",
    archiveDownloadAudio: "Unduh audio",
    archiveProcessingTitle: "Pemrosesan",
    archiveProcessingQueued: "Menunggu pemrosesan transkrip dan ringkasan",
    archiveProcessingRunning: "Membuat transkrip dan ringkasan multibahasa",
    archiveProcessingReady: "Transkrip dan ringkasan sudah siap",
    archiveProcessingFailed: "Pemrosesan perlu diperiksa",
    archiveProcessingError: "Pemrosesan gagal. Muat ulang nanti atau minta administrator untuk mencobanya lagi.",
    archiveRetryProcessing: "Coba proses lagi",
    archiveRetryingProcessing: "Mencoba lagi…",
    archiveTranscriptTitle: "Transkrip pembicara",
    archiveTranscriptPending: "Transkrip dengan pembicara akan muncul setelah pemrosesan selesai.",
    archiveTranscriptEmpty: "Transkrip tidak tersedia untuk rapat ini.",
    archiveDownloadTranscript: "Unduh transkrip",
    archiveViewTranscript: "Buka transkrip pembicara",
    archiveBackToArchive: "Kembali ke arsip rapat",
    archivePlayFromTimestamp: "Putar rekaman dari",
    archiveRenameSpeaker: "Ubah nama pembicara",
    archiveSpeakerNameLabel: "Nama pembicara",
    archiveSaveSpeaker: "Simpan nama pembicara",
    archiveCancel: "Batal",
    archiveSpeakerSaveError: "Nama pembicara tidak dapat disimpan.",
    archiveSummaryTitle: "Ringkasan rapat",
    archiveSummaryPending: "Ringkasan bahasa ini masih disiapkan.",
    archiveSummaryEmpty: "Ringkasan tidak tersedia dalam bahasa ini.",
    archiveDownloadSummary: "Unduh ringkasan",
    archiveOverview: "Ikhtisar",
    archiveKeyPoints: "Poin utama",
    archiveDecisions: "Keputusan",
    archiveActionItems: "Tindak lanjut",
    archiveOpenQuestions: "Pertanyaan terbuka",
    archiveStatusRecording: "Merekam",
    archiveStatusFinalizing: "Menyelesaikan",
    archiveStatusReady: "Siap",
    archiveStatusFailed: "Gagal",
    archiveStatusProcessing: "Memproses",
    archiveLocaleEnglish: "English",
    archiveLocaleIndonesian: "Bahasa Indonesia",
    archiveLocaleChinese: "简体中文",
    archiveLocaleThai: "ไทย",
  },
  zh: {
    appName: "同桌翻译",
    metaDescription: "用您能理解的语言，跟上同桌的实时对话。",
    setupEyebrow: "同桌 · 同一场对话",
    outputLanguagePrompt: "您想听懂哪种语言？",
    outputLanguageHelp: "选择您想阅读或收听的语言。",
    textOnly: "仅文字",
    textOnlyDescription: "在屏幕上安静地查看翻译。",
    textAndAudio: "文字和音频",
    textAndAudioDescription: "翻译出现时，同时阅读并收听。",
    headphonesRecommended: "建议使用耳机，以免在桌边产生回声。",
    audioDevices: "音频设备",
    audioDevicesHelp: "开始前请检查麦克风和收听输出。",
    microphone: "麦克风",
    audioOutput: "音频输出",
    systemDefault: "系统默认",
    systemOutputGuidance: "翻译音频将使用此处选择的输出。",
    outputSelectionUnsupported: "请在控制中心或系统设置中选择音频输出。",
    inputLevel: "输入音量",
    testMicrophone: "测试麦克风",
    testAudio: "测试音频",
    startListening: "开始聆听",
    preparingMicrophone: "正在准备麦克风…",
    connectingTranslation: "正在连接…",
    stopListening: "停止聆听",
    liveTranslation: "实时翻译",
    transcriptEmpty: "有人说话时，翻译会显示在这里。",
    muteMicrophone: "关闭麦克风",
    unmuteMicrophone: "打开麦克风",
    turnAudioOn: "打开音频",
    turnAudioOff: "关闭音频",
    showText: "显示文字",
    hideText: "隐藏文字",
    volume: "音量",
    changeLanguage: "更改语言",
    leave: "离开",
    retry: "重试",
    tryAgain: "再试一次",
    statusIdle: "就绪",
    statusConnecting: "正在连接",
    statusListening: "正在聆听",
    statusReconnecting: "正在重新连接",
    statusMuted: "麦克风已关闭",
    statusError: "连接失败",
    errorMicrophonePermission: "请允许使用麦克风，以便翻译对话。",
    errorMicrophoneTimeout: "等待麦克风权限的时间过长。请检查浏览器提示或网站设置；响应后此页面会自动继续。",
    errorMicrophoneBusy: "麦克风不可用或正被其他应用使用。请关闭其他应用中的麦克风，然后重试。",
    errorUnsupportedBrowser: "此浏览器无法访问麦克风。请在 Safari 或 Chrome 中打开此链接，然后重试。",
    errorOffline: "设备似乎已离线。请重新连接网络，然后重试。",
    errorNoMicrophone: "未找到麦克风。请连接后重试。",
    errorConnection: "翻译连接已中断。请重试。",
    errorConnectionTimeout: "翻译未能及时连接。请检查网络连接，然后重试。",
    errorAudioPlayback: "无法播放音频。请检查输出后重试。",
    navigationLabel: "导航",
    openHostRecorder: "打开主持人录音",
    openParticipantTranslator: "参与者翻译",
    openMeetingArchive: "会议档案",
    backToHostRecorder: "返回主持人录音",
    archiveSignInRequired: "请先在主持人录音页面登录，以打开受保护的会议档案。",
    recordingSavedMessage: "会议已保存。录音已上传并完成，正在生成转写和摘要。",
    viewMeeting: "查看会议",
    dismissNotification: "关闭通知",
    hostAccessTitle: "主持人录音",
    hostAccessDescription: "超级用户可以创建受保护的会议录音。",
    hostEmailLabel: "电子邮箱",
    hostPasswordLabel: "密码",
    hostLoginAction: "登录",
    hostLoggingInAction: "正在登录…",
    hostSignedInLabel: "当前登录",
    hostLogoutAction: "退出登录",
    hostLoggingOutAction: "正在退出…",
    hostMeetingTitleLabel: "会议标题",
    hostMeetingTitlePlaceholder: "例如：供应商讨论",
    hostConsentLabel: "同桌所有参与者均已同意录音。",
    hostConsentHelp: "会议录音完成前，请保持手机解锁并停留在此页面。",
    hostLoginError: "主持人登录失败。请检查信息后重试。",
    recordingPreparingStatus: "正在准备录音",
    recordingActiveStatus: "REC · 正在录音",
    recordingUploadingStatus: "正在完成上传",
    recordingFinalizingStatus: "正在完成录音",
    recordingReadyStatus: "录音已就绪",
    recordingFailedStatus: "录音需要处理",
    recordingElapsedLabel: "已录时长",
    recordingPendingChunksLabel: "待上传",
    recordingAcknowledgedChunkLabel: "已存片段",
    recordingNoAcknowledgedChunk: "暂无",
    recordingPlaybackLabel: "受保护的会议录音",
    recordingRetryAction: "重试完成录音",
    recordingConfirmStop: "停止并完成本次会议录音吗？完成前请保持此页面打开。",
    recordingErrorStart: "无法启动受保护的录音。请保持此页面打开并重试。",
    recordingErrorUnsupported: "此浏览器无法创建会议录音。请使用最新版 Chrome 或 Safari。",
    recordingErrorMicrophone: "录音麦克风已断开。请立即停止并保存会议。",
    recordingErrorUpload: "部分音频尚未存储。请保持此页面打开，然后停止录音或重试上传。",
    recordingErrorRecorder: "浏览器录音意外停止。请立即停止并保存会议。",
    recordingErrorNoAudio: "录音中没有音频。请检查麦克风后重试。",
    recordingErrorFinalize: "录音暂时无法完成。请保持此页面打开并重试。",
    recordingWarningStorage: "本地恢复存储不可用。所有待上传片段存储完成前，请保持此页面打开。",
    recordingRecoveryTitle: "发现未完成的录音",
    recordingRecoveryDescription: "恢复已存音频并完成录音：",
    recordingRecoveryAction: "恢复并完成",
    recordingRecoveryRemaining: "个未完成录音待处理",
    recordingDiscardAction: "丢弃本地副本",
    recordingDiscardConfirm: "要丢弃本地恢复副本吗？尚未上传的音频将无法恢复。",
    archiveTitle: "会议档案",
    archiveDescription: "供所有超级用户共享的录音、转写和摘要。",
    archiveRefresh: "刷新",
    archiveLoading: "正在加载会议档案…",
    archiveLoadError: "无法加载会议档案。",
    archiveEmpty: "暂无已录制的会议。",
    archiveNoResults: "没有符合搜索条件的会议。",
    archiveSelectPrompt: "请选择会议以查看受保护的记录。",
    archiveDetailError: "无法加载此会议记录。",
    archiveSearchLabel: "搜索会议",
    archiveSearchPlaceholder: "按标题、日期或状态搜索",
    archiveStatusFilterLabel: "按状态筛选会议",
    archiveStatusAll: "所有状态",
    archiveAudioTitle: "录音",
    archiveAudioUnavailable: "受保护的录音尚未就绪。",
    archiveDownloadAudio: "下载音频",
    archiveProcessingTitle: "处理状态",
    archiveProcessingQueued: "正在等待生成转写和摘要",
    archiveProcessingRunning: "正在生成转写和多语言摘要",
    archiveProcessingReady: "转写和摘要已就绪",
    archiveProcessingFailed: "处理需要检查",
    archiveProcessingError: "处理失败。请稍后刷新，或请管理员重试。",
    archiveRetryProcessing: "重试处理",
    archiveRetryingProcessing: "正在重试…",
    archiveTranscriptTitle: "分角色转写",
    archiveTranscriptPending: "处理完成后将显示区分说话人的转写。",
    archiveTranscriptEmpty: "此会议暂无转写。",
    archiveDownloadTranscript: "下载转写",
    archiveViewTranscript: "打开分角色转写",
    archiveBackToArchive: "返回会议档案",
    archivePlayFromTimestamp: "从此处播放录音",
    archiveRenameSpeaker: "重命名说话人",
    archiveSpeakerNameLabel: "说话人姓名",
    archiveSaveSpeaker: "保存说话人姓名",
    archiveCancel: "取消",
    archiveSpeakerSaveError: "无法保存说话人姓名。",
    archiveSummaryTitle: "会议摘要",
    archiveSummaryPending: "此语言的摘要仍在生成中。",
    archiveSummaryEmpty: "此语言暂无摘要。",
    archiveDownloadSummary: "下载摘要",
    archiveOverview: "概述",
    archiveKeyPoints: "要点",
    archiveDecisions: "决定",
    archiveActionItems: "行动事项",
    archiveOpenQuestions: "待解决问题",
    archiveStatusRecording: "录音中",
    archiveStatusFinalizing: "正在完成",
    archiveStatusReady: "已就绪",
    archiveStatusFailed: "失败",
    archiveStatusProcessing: "处理中",
    archiveLocaleEnglish: "English",
    archiveLocaleIndonesian: "Bahasa Indonesia",
    archiveLocaleChinese: "简体中文",
    archiveLocaleThai: "ไทย",
  },
  th: {
    appName: "Table Interpreter",
    metaDescription: "ติดตามบทสนทนาที่โต๊ะเดียวกันในภาษาที่คุณเข้าใจ",
    setupEyebrow: "โต๊ะเดียวกัน · สนทนาร่วมกัน",
    outputLanguagePrompt: "คุณต้องการเข้าใจภาษาอะไร?",
    outputLanguageHelp: "เลือกภาษาที่คุณต้องการอ่านหรือฟัง",
    textOnly: "ข้อความเท่านั้น",
    textOnlyDescription: "ติดตามคำแปลบนหน้าจออย่างเงียบ ๆ",
    textAndAudio: "ข้อความและเสียง",
    textAndAudioDescription: "อ่านและฟังคำแปลได้ทันทีที่คำแปลมาถึง",
    headphonesRecommended: "แนะนำให้ใช้หูฟังเพื่อป้องกันเสียงสะท้อนที่โต๊ะ",
    audioDevices: "อุปกรณ์เสียง",
    audioDevicesHelp: "ตรวจสอบไมโครโฟนและเอาต์พุตเสียงก่อนเริ่ม",
    microphone: "ไมโครโฟน",
    audioOutput: "เอาต์พุตเสียง",
    systemDefault: "ค่าเริ่มต้นของระบบ",
    systemOutputGuidance: "เสียงคำแปลจะเล่นผ่านเอาต์พุตที่เลือกไว้ที่นี่",
    outputSelectionUnsupported: "เลือกเอาต์พุตเสียงในศูนย์ควบคุมหรือการตั้งค่าระบบ",
    inputLevel: "ระดับสัญญาณเข้า",
    testMicrophone: "ทดสอบไมโครโฟน",
    testAudio: "ทดสอบเสียง",
    startListening: "เริ่มฟัง",
    preparingMicrophone: "กำลังเตรียมไมโครโฟน…",
    connectingTranslation: "กำลังเชื่อมต่อ…",
    stopListening: "หยุดฟัง",
    liveTranslation: "คำแปลสด",
    transcriptEmpty: "คำแปลจะปรากฏที่นี่เมื่อมีคนพูด",
    muteMicrophone: "ปิดไมโครโฟน",
    unmuteMicrophone: "เปิดไมโครโฟน",
    turnAudioOn: "เปิดเสียง",
    turnAudioOff: "ปิดเสียง",
    showText: "แสดงข้อความ",
    hideText: "ซ่อนข้อความ",
    volume: "ระดับเสียง",
    changeLanguage: "เปลี่ยนภาษา",
    leave: "ออก",
    retry: "ลองใหม่",
    tryAgain: "ลองอีกครั้ง",
    statusIdle: "พร้อม",
    statusConnecting: "กำลังเชื่อมต่อ",
    statusListening: "กำลังฟัง",
    statusReconnecting: "กำลังเชื่อมต่อใหม่",
    statusMuted: "ปิดไมโครโฟนแล้ว",
    statusError: "เชื่อมต่อล้มเหลว",
    errorMicrophonePermission: "อนุญาตให้เข้าถึงไมโครโฟนเพื่อแปลบทสนทนา",
    errorMicrophoneTimeout: "การอนุญาตใช้ไมโครโฟนใช้เวลานานเกินไป ตรวจสอบข้อความแจ้งจากเบราว์เซอร์หรือการตั้งค่าเว็บไซต์ หน้าจอนี้จะทำงานต่อเมื่อคุณตอบกลับ",
    errorMicrophoneBusy: "ไมโครโฟนไม่พร้อมใช้งานหรือกำลังถูกแอปอื่นใช้ ปิดการใช้งานในแอปนั้นแล้วลองอีกครั้ง",
    errorUnsupportedBrowser: "เบราว์เซอร์นี้เข้าถึงไมโครโฟนไม่ได้ เปิดลิงก์นี้ใน Safari หรือ Chrome แล้วลองอีกครั้ง",
    errorOffline: "ดูเหมือนว่าคุณออฟไลน์อยู่ เชื่อมต่ออินเทอร์เน็ตแล้วลองอีกครั้ง",
    errorNoMicrophone: "ไม่พบไมโครโฟน เชื่อมต่อไมโครโฟนแล้วลองอีกครั้ง",
    errorConnection: "การเชื่อมต่อคำแปลถูกขัดจังหวะ ลองอีกครั้ง",
    errorConnectionTimeout: "เชื่อมต่อบริการแปลไม่ทันเวลา ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตแล้วลองอีกครั้ง",
    errorAudioPlayback: "เล่นเสียงไม่ได้ ตรวจสอบเอาต์พุตแล้วลองอีกครั้ง",
    navigationLabel: "การนำทาง",
    openHostRecorder: "เปิดตัวบันทึกของโฮสต์",
    openParticipantTranslator: "ตัวแปลสำหรับผู้เข้าร่วม",
    openMeetingArchive: "คลังการประชุม",
    backToHostRecorder: "กลับไปยังตัวบันทึกของโฮสต์",
    archiveSignInRequired: "ลงชื่อเข้าใช้ในตัวบันทึกของโฮสต์เพื่อเปิดคลังการประชุมที่มีการป้องกัน",
    recordingSavedMessage: "บันทึกการประชุมแล้ว ไฟล์บันทึกถูกอัปโหลดและเสร็จสมบูรณ์ ขณะนี้กำลังประมวลผลบทถอดเสียงและสรุป",
    viewMeeting: "ดูการประชุม",
    dismissNotification: "ปิดการแจ้งเตือน",
    hostAccessTitle: "ตัวบันทึกของโฮสต์",
    hostAccessDescription: "ผู้ใช้ระดับสูงสามารถสร้างไฟล์บันทึกการประชุมที่มีการป้องกันได้",
    hostEmailLabel: "อีเมล",
    hostPasswordLabel: "รหัสผ่าน",
    hostLoginAction: "ลงชื่อเข้าใช้",
    hostLoggingInAction: "กำลังลงชื่อเข้าใช้…",
    hostSignedInLabel: "ลงชื่อเข้าใช้ในชื่อ",
    hostLogoutAction: "ออกจากระบบ",
    hostLoggingOutAction: "กำลังออกจากระบบ…",
    hostMeetingTitleLabel: "ชื่อการประชุม",
    hostMeetingTitlePlaceholder: "เช่น การหารือกับซัพพลายเออร์",
    hostConsentLabel: "ทุกคนที่โต๊ะยินยอมให้บันทึกเสียงแล้ว",
    hostConsentHelp: "ปลดล็อกโทรศัพท์และเปิดหน้าจอนี้ไว้จนกว่าการประชุมจะเสร็จสมบูรณ์",
    hostLoginError: "ลงชื่อเข้าใช้โฮสต์ไม่สำเร็จ ตรวจสอบข้อมูลแล้วลองอีกครั้ง",
    recordingPreparingStatus: "กำลังเตรียมบันทึก",
    recordingActiveStatus: "REC · กำลังบันทึก",
    recordingUploadingStatus: "กำลังอัปโหลดให้เสร็จ",
    recordingFinalizingStatus: "กำลังดำเนินการบันทึกให้เสร็จ",
    recordingReadyStatus: "ไฟล์บันทึกพร้อมแล้ว",
    recordingFailedStatus: "ไฟล์บันทึกต้องได้รับการตรวจสอบ",
    recordingElapsedLabel: "เวลาที่ผ่านไป",
    recordingPendingChunksLabel: "รอดำเนินการ",
    recordingAcknowledgedChunkLabel: "ส่วนที่จัดเก็บแล้ว",
    recordingNoAcknowledgedChunk: "ยังไม่มี",
    recordingPlaybackLabel: "ไฟล์บันทึกการประชุมที่มีการป้องกัน",
    recordingRetryAction: "ลองดำเนินการให้เสร็จอีกครั้ง",
    recordingConfirmStop: "หยุดและดำเนินการบันทึกการประชุมนี้ให้เสร็จหรือไม่? เปิดหน้าจอนี้ไว้จนกว่าจะเสร็จ",
    recordingErrorStart: "เริ่มการบันทึกที่มีการป้องกันไม่ได้ เปิดหน้าจอนี้ไว้แล้วลองอีกครั้ง",
    recordingErrorUnsupported: "เบราว์เซอร์นี้สร้างไฟล์บันทึกการประชุมไม่ได้ โปรดใช้ Chrome หรือ Safari เวอร์ชันล่าสุด",
    recordingErrorMicrophone: "ไมโครโฟนที่ใช้บันทึกถูกตัดการเชื่อมต่อ หยุดและบันทึกการประชุมตอนนี้",
    recordingErrorUpload: "เสียงบางส่วนยังไม่ได้จัดเก็บ เปิดหน้าจอนี้ไว้ จากนั้นหยุดหรือลองอัปโหลดอีกครั้ง",
    recordingErrorRecorder: "ตัวบันทึกของเบราว์เซอร์หยุดทำงานโดยไม่คาดคิด หยุดและบันทึกการประชุมตอนนี้",
    recordingErrorNoAudio: "การบันทึกไม่มีเสียง ตรวจสอบไมโครโฟนก่อนลองอีกครั้ง",
    recordingErrorFinalize: "ยังดำเนินการบันทึกให้เสร็จไม่ได้ เปิดหน้าจอนี้ไว้แล้วลองอีกครั้ง",
    recordingWarningStorage: "พื้นที่จัดเก็บสำหรับการกู้คืนในเครื่องไม่พร้อมใช้งาน เปิดหน้าจอนี้ไว้จนกว่าส่วนที่รอดำเนินการทั้งหมดจะถูกจัดเก็บ",
    recordingRecoveryTitle: "พบไฟล์บันทึกที่ยังไม่เสร็จ",
    recordingRecoveryDescription: "กู้คืนเสียงที่จัดเก็บไว้และดำเนินการให้เสร็จ:",
    recordingRecoveryAction: "กู้คืนและดำเนินการให้เสร็จ",
    recordingRecoveryRemaining: "ไฟล์บันทึกที่ยังไม่เสร็จคงเหลือ",
    recordingDiscardAction: "ลบสำเนาในเครื่อง",
    recordingDiscardConfirm: "ลบสำเนาสำหรับกู้คืนในเครื่องหรือไม่? เสียงที่ยังไม่ได้อัปโหลดจะกู้คืนไม่ได้",
    archiveTitle: "คลังการประชุม",
    archiveDescription: "ไฟล์บันทึก บทถอดเสียง และสรุปที่แชร์สำหรับผู้ใช้ระดับสูงทุกคน",
    archiveRefresh: "รีเฟรช",
    archiveLoading: "กำลังโหลดคลังการประชุม…",
    archiveLoadError: "โหลดคลังการประชุมไม่ได้",
    archiveEmpty: "ยังไม่มีการประชุมที่บันทึกไว้",
    archiveNoResults: "ไม่พบการประชุมที่ตรงกับการค้นหา",
    archiveSelectPrompt: "เลือกการประชุมเพื่อดูบันทึกที่มีการป้องกัน",
    archiveDetailError: "โหลดบันทึกการประชุมนี้ไม่ได้",
    archiveSearchLabel: "ค้นหาการประชุม",
    archiveSearchPlaceholder: "ค้นหาชื่อ วันที่ หรือสถานะ",
    archiveStatusFilterLabel: "กรองการประชุมตามสถานะ",
    archiveStatusAll: "ทุกสถานะ",
    archiveAudioTitle: "ไฟล์บันทึก",
    archiveAudioUnavailable: "ไฟล์บันทึกที่มีการป้องกันยังไม่พร้อม",
    archiveDownloadAudio: "ดาวน์โหลดเสียง",
    archiveProcessingTitle: "การประมวลผล",
    archiveProcessingQueued: "กำลังรอประมวลผลบทถอดเสียงและสรุป",
    archiveProcessingRunning: "กำลังสร้างบทถอดเสียงและสรุปหลายภาษา",
    archiveProcessingReady: "บทถอดเสียงและสรุปพร้อมแล้ว",
    archiveProcessingFailed: "การประมวลผลต้องได้รับการตรวจสอบ",
    archiveProcessingError: "ประมวลผลไม่สำเร็จ รีเฟรชภายหลังหรือขอให้ผู้ดูแลระบบลองอีกครั้ง",
    archiveRetryProcessing: "ลองประมวลผลอีกครั้ง",
    archiveRetryingProcessing: "กำลังลองอีกครั้ง…",
    archiveTranscriptTitle: "บทถอดเสียงแยกตามผู้พูด",
    archiveTranscriptPending: "บทถอดเสียงแยกตามผู้พูดจะปรากฏเมื่อประมวลผลเสร็จ",
    archiveTranscriptEmpty: "ไม่มีบทถอดเสียงสำหรับการประชุมนี้",
    archiveDownloadTranscript: "ดาวน์โหลดบทถอดเสียง",
    archiveViewTranscript: "เปิดบทถอดเสียงแยกตามผู้พูด",
    archiveBackToArchive: "กลับไปยังคลังการประชุม",
    archivePlayFromTimestamp: "เล่นไฟล์บันทึกตั้งแต่",
    archiveRenameSpeaker: "เปลี่ยนชื่อผู้พูด",
    archiveSpeakerNameLabel: "ชื่อผู้พูด",
    archiveSaveSpeaker: "บันทึกชื่อผู้พูด",
    archiveCancel: "ยกเลิก",
    archiveSpeakerSaveError: "บันทึกชื่อผู้พูดไม่ได้",
    archiveSummaryTitle: "สรุปการประชุม",
    archiveSummaryPending: "กำลังจัดเตรียมสรุปในภาษานี้",
    archiveSummaryEmpty: "ไม่มีสรุปในภาษานี้",
    archiveDownloadSummary: "ดาวน์โหลดสรุป",
    archiveOverview: "ภาพรวม",
    archiveKeyPoints: "ประเด็นสำคัญ",
    archiveDecisions: "ข้อสรุปที่ตัดสินใจ",
    archiveActionItems: "รายการที่ต้องดำเนินการ",
    archiveOpenQuestions: "คำถามที่ยังไม่มีข้อสรุป",
    archiveStatusRecording: "กำลังบันทึก",
    archiveStatusFinalizing: "กำลังดำเนินการให้เสร็จ",
    archiveStatusReady: "พร้อม",
    archiveStatusFailed: "ล้มเหลว",
    archiveStatusProcessing: "กำลังประมวลผล",
    archiveLocaleEnglish: "English",
    archiveLocaleIndonesian: "Bahasa Indonesia",
    archiveLocaleChinese: "简体中文",
    archiveLocaleThai: "ไทย",
  },
};
